import {
    IonButton,
    IonCol,
    IonRow,
} from "@ionic/react";
import React, { useContext, useEffect, useState } from "react";
import { Barn, Horse, Owner } from "../../../models";
import ErrorAlert from "../../Errors/ErrorAlert";
import SuccessBanner from "../../Banners/SuccessBanner";
import { CreateOwnerInput, UpdateHorseInput } from "../../../API";
import { createOwner, getOwnerById } from "../../../utilities/owner/Owner";
import { updateHorse } from "../../../utilities/horse/Horse";
import { Label } from "reactstrap";
import SelectOrCreateOwner from "../../Owner/SelectOrCreateOwner";
import { formatSingleOwner, formattedOption, getOwnersCreatedByUserOrInCurrentBarn } from "../../../utilities/owner/SelectOrCreateOwnerUtils";
import { PersonContext } from "../../../context/PersonContext";
import Spinner from "../../Spinners/Spinner";

interface _Props {
    horse: Horse
    barn?: (Barn | null | undefined)
}

const EditHorseOwnerForm: React.FC<_Props> = ({horse, barn}) => {
    const user = useContext(PersonContext);

    // Owner State
    const [ownerName, setOwnerName] = useState<string | null | undefined>(); 
    const [owner, setOwner] = useState<Owner | null | undefined>();

    // Basic State
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    // Dropdown State
    const [userOwnerOptions, setUserOwnerOptions] = useState<formattedOption[] | null | undefined>();
    const [barnOwnerOptions, setBarnOwnerOptions] = useState<formattedOption[] | null | undefined>();
    const [horseOwnerOptions, setHorseOwnerOptions] = useState<formattedOption[] | null | undefined>();
    
    // Load inital User and Barn options to populate the dropdown
    useEffect(() => {
        const getData = async () => {
            setIsLoading(true);
            setError("");
            setSuccess("");
            const result = await getOwnersCreatedByUserOrInCurrentBarn(user, barn, userOwnerOptions || undefined);

            const createdByOptions = result.createdBy;
            const barnOptions = result.barn;

            // Check if the horse has an owner assigned to it that is not in this barn or created by this user
            if (horse.ownerId) addHorseOwnerToOptionsIfNotAlreadyThere(horse.ownerId, horse.owner);

            setUserOwnerOptions(createdByOptions);
            setBarnOwnerOptions(barnOptions);
            setIsLoading(false);
        };

        getData();
    }, [barn]);
    
    useEffect(() => {
        if (horse && horse.owner) {
            setOwner(horse.owner);
        } else if (horse && horse.ownerId) {
            getOwner(horse.ownerId);
        } else {
            clearForm();
        }
    }, [horse]);

    const clearForm = () => {
        setOwnerName(undefined);
        setOwner(undefined);
        setError("");
        setSuccess("");
    }

    const addHorseOwnerToOptionsIfNotAlreadyThere = async (newOwnerId: string, newOwner?: (Owner | null | undefined)) => {
            let foundDuplicateOwner = false;
            if (userOwnerOptions && userOwnerOptions.length > 0) {
                for (let i = 0; i < userOwnerOptions.length; i++) {
                    const userOwner = userOwnerOptions[i];
                    if (userOwner.value === newOwnerId) {
                        foundDuplicateOwner = true;
                        break;
                    }
                }
            }
            if (!foundDuplicateOwner && barnOwnerOptions && barnOwnerOptions.length > 0) {
                for (let i = 0; i < barnOwnerOptions.length; i++) {
                    const barnOwner = barnOwnerOptions[i];
                    if (barnOwner.value === newOwnerId) {
                        foundDuplicateOwner = true;
                        break;
                    }
                }
            }
            if (!foundDuplicateOwner) {
                let currentOwner: Owner | null | undefined = newOwner;
                if (!currentOwner && newOwnerId) {
                    const queryOwnerForHorse = await getOwnerById(newOwnerId);
                    if (queryOwnerForHorse.isSuccess) {
                        const result: Owner = queryOwnerForHorse.result;
                        currentOwner = result;
                    }
                }
                if (currentOwner) {
                    const formattedOwner = await formatSingleOwner(currentOwner);
                    setHorseOwnerOptions(formattedOwner ? [formattedOwner] : undefined);
                }   
            }
    }

    const getOwner = async (id: string) => {
        const queryResult = await getOwnerById(id);
        if (queryResult.isSuccess) {
            setOwner(queryResult.result);
        }
    }

    const handleEditHorse = async () => {
        setIsLoading(true);
        setError("");
        setSuccess("");

        // Track the updatedOwner
        let updatedOwner: (Owner | null | undefined) = owner;

        if (!updatedOwner && ownerName) {
            const createInput: CreateOwnerInput = {
                name: ownerName,
                barnId: barn?.id || null,
                createdBy: user.id
            };
            const createResult = await createOwner(createInput);
            if (createResult.isSuccess) {
                const newOwner: Owner = createResult.result;
                updatedOwner = newOwner;
                // Next, add this new owner to the Barn owners category
                const formattedOption = await formatSingleOwner(newOwner, user);
                const updatedBarnOwners: formattedOption[] = (barnOwnerOptions || [])?.concat([formattedOption]);
                const sorted: formattedOption[] = updatedBarnOwners.sort((a, b) => a.ownerName.trim().localeCompare(b.ownerName.trim()));
                setBarnOwnerOptions(sorted);
            }
        }

        const input: UpdateHorseInput = {
            id: horse.id,
            ownerId: updatedOwner?.id || null,
            ownerName: updatedOwner?.name || ""
        };
        const updateResult = await updateHorse(input);
        if (updateResult.isSuccess) {
            setSuccess("Successfully saved the updates.");
            setOwner(updatedOwner);
        } else {
            setError(updateResult.message);
        }
        setIsLoading(false);
    }

    const handleSelectedOwner = async (owner?: Owner, ownerName?: string) => {
        setOwner(owner);
        setOwnerName(ownerName);
        if (owner) addHorseOwnerToOptionsIfNotAlreadyThere(owner.id, owner);
    };

    return (
        <>
            {success && <SuccessBanner success={success} width="12" />}
            {error && <ErrorAlert error={error} width="12" />}
            {isLoading ? 
                <IonRow>
                    <IonCol className="ion-text-center ion-align-self-center">
                        <Spinner />
                    </IonCol>
                </IonRow>
                :
                <IonRow className="ion-justify-content-center">
                    <IonCol size="12">
                        <Label>
                            Name
                        </Label>
                        <SelectOrCreateOwner
                            userOwnerOptions={userOwnerOptions}
                            barnOwnerOptions={barnOwnerOptions}
                            horseOwnerOptions={horseOwnerOptions}
                            selectedHorse={horse}
                            selectedBarn={barn} 
                            selectedValue={owner?.id} 
                            onSelect={(owner?: Owner, ownerName?: string) => {
                                handleSelectedOwner(owner, ownerName);
                            }}
                        />
                    </IonCol>
                    <IonCol size="12" className="ion-text-center">
                        <IonButton
                            color="success"
                            onClick={() => handleEditHorse()}
                        >
                            Save
                        </IonButton>
                    </IonCol>
                </IonRow>
            }
        </>
    );
};

export default EditHorseOwnerForm;